// THE ONE SOURCE FOR "IS ANYTHING ASKING FOR ME RIGHT NOW".
//
// The bell in the top bar and the red dots in the sidebar are two renderings of the same
// fact, so they read the same object from here. Neither fetches. If each had loaded its
// own, they would eventually have disagreed on screen — a dot beside לידים with an empty
// bell, and nothing to say which was right.
//
// A PLAIN DATE CHECK AT LOAD TIME. No polling, no socket, no push: one GET when the app
// mounts, and the server compares dates once (calculations/dueTasks). Leaving the tab
// open across midnight therefore leaves the counts as they were until something reloads
// them — which is the honest behaviour for a load-time check and is why `reload()` is
// exposed and called by every write that can change a deadline.
import { createContext, useContext } from 'react';
import useAsync from '../hooks/useAsync';
import { alertsApi } from '../api';

// Exported for ONE caller: NotificationBell.test.js, which supplies a fixture directly
// so the bell can be rendered without a fetch. Application code uses `useAlerts()`.
export const AlertsContext = createContext(null);

const EMPTY = {
  tasks: { rows: [], counts: { overdue: 0, today: 0 }, byBoard: {} },
  // The second source, kept a sibling of tasks rather than merged into it: a task is
  // something to produce, a lead follow-up is contact tracking. See
  // server/calculations/leadFollowUps.
  leads: { rows: [], counts: { overdue: 0, today: 0 } },
  // The third source, and the only one that is not about a date: work a content writer
  // sent in that nobody has opened yet (migration 064). No counts object, because there
  // is no overdue/today split to make — a file has either been looked at or it has not.
  uploads: { rows: [], count: 0 },
  money: {
    overdueCount: 0, overdueTotal: 0, openCount: 0, expectedMonths: 0, expectedCharges: 0, waitingCount: 0,
  },
};

// THE MONEY DOT, in three colours, louder first:
//   red    something is LATE — any charge past its due date, whatever it was raised for
//   amber  something TO DO — a retainer month with no amount to charge, or a cost to
//          charge on. The counts are the money screen headline's own (server:
//          collectibles totals), so the dot lights exactly when that headline's to-do line
//          has something in it.
//   blue   money WAITING — owed and not late yet (068). Every amount owed is on the books
//          by itself now, so the owners must always see that something is uncollected.
//   off    nothing owed at all.
// Exported for its test; application code reads it through navLevel('money').
export const moneyLevel = (money) => {
  if (!money) return null;
  if (money.overdueCount > 0) return 'overdue';
  if ((money.expectedMonths || 0) + (money.expectedCharges || 0) > 0) return 'today';
  // BLUE (068): money owed and not late yet. Every amount owed is on the books by itself
  // now, so this is the standing reminder that something is still uncollected — the
  // quietest colour, shown only when nothing is late and nothing is waiting on us.
  if ((money.waitingCount || 0) > 0) return 'waiting';
  return null;
};

// 'overdue' | 'today' | null for one nav key — the sidebar dot's colour, derived HERE and
// not in the sidebar, so the bell and the dots cannot apply different thresholds to the
// same numbers. Overdue wins over today: the louder state is the one worth showing.
//
// The keys are the server's board names plus 'money'. There is no key for the orphan
// bucket ('none') and that is deliberate — those tasks belong to no page, so no nav item
// can honestly carry their dot. The bell is where they surface.
const levelOf = (counts) => {
  if (!counts) return null;
  if (counts.overdue > 0) return 'overdue';
  if (counts.today > 0) return 'today';
  return null;
};

// The louder of two levels. The לידים dot now answers ONE question with TWO inputs —
// "is anything on this screen asking for me" — because a lead can hold a late task AND
// a late follow-up, and the sidebar has one dot per item by design. Red wins over amber,
// and either wins over nothing.
const louder = (a, b) => {
  if (a === 'overdue' || b === 'overdue') return 'overdue';
  if (a === 'today' || b === 'today') return 'today';
  return null;
};

export const AlertsProvider = ({ children }) => {
  const { data, loading, reload } = useAsync(() => alertsApi.get());
  const alerts = data || EMPTY;

  const value = {
    loading,
    reload,
    tasks: alerts.tasks,
    leads: alerts.leads,
    uploads: alerts.uploads,
    money: alerts.money,
    // One number for the bell's badge: everything that has come due, of either kind.
    // Added rather than shown as two figures, because a bell reporting "3 / 2" is a bell
    // that has to be decoded before it can be acted on.
    // One number for the bell badge: everything asking for attention, of any kind.
    // Added rather than shown as separate figures, because a bell reporting "3 / 2 / 1"
    // has to be decoded before it can be acted on.
    //
    // Unseen uploads are IN the badge, deliberately. They are the one thing here that
    // nobody in the office set in motion — a file arrived from outside — so a badge that
    // ignored them would leave the manager with no signal at all, which is the entire
    // point of the feature. They are NOT in the colour rule below: red means something
    // is late, and a file arriving is not late.
    total: alerts.tasks.counts.overdue + alerts.tasks.counts.today
      + alerts.leads.counts.overdue + alerts.leads.counts.today
      + alerts.uploads.count,
    // Nav-item key → dot level.
    //   'leads'  — late tasks on leads OR follow-ups that have come due (see `louder`)
    //   'money'  — red for anything late, amber for work waiting; see moneyLevel
    navLevel: (key) => {
      if (key === 'money') return moneyLevel(alerts.money);
      const fromTasks = levelOf(alerts.tasks.byBoard[key]);
      if (key !== 'leads') return fromTasks;
      return louder(fromTasks, levelOf(alerts.leads.counts));
    },
  };

  return <AlertsContext.Provider value={value}>{children}</AlertsContext.Provider>;
};

export const useAlerts = () => useContext(AlertsContext);

// For a write that can change what the sidebar dots say: re-read now, not on the next
// full load. A no-op where no provider is mounted (tests render components without one).
export const useReloadAlerts = () => {
  const alerts = useAlerts();
  return () => { if (alerts) alerts.reload(); };
};
